/* ============================================================================
  JMT Pref Engine v2.1 (Standard Cut Preference Guard)
  - Single source of truth for preference state (cut/size)
  - Persists to localStorage when allowed; falls back to cookie
  - Emits (when state actually changes): jmt:pref:change
  - Provides setSilent() for apply-only code (no event emission)
============================================================================ */

(function () {
  'use strict';

  var KEY = 'jmt:pref:v1';
  var EVT = 'jmt:pref:change';

  // Keep cookie mirrored so we have a fallback when storage is blocked.
  // Using same KEY keeps backward compatibility with what you already deployed.
  var COOKIE_DAYS = 365;

  function safeJsonParse(s) {
    try { return JSON.parse(s); } catch (e) { return null; }
  }

  function cookieGet(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function cookieSet(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + (days || COOKIE_DAYS) * 24 * 60 * 60 * 1000);
      document.cookie =
        name + '=' + encodeURIComponent(value) +
        '; expires=' + d.toUTCString() +
        '; path=/' +
        '; SameSite=Lax';
    } catch (e) {}
  }

  function cookieDel(name) {
    try {
      document.cookie =
        name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function storageGet() {
    try { return window.localStorage.getItem(KEY); } catch (e) { return null; }
  }

  function storageSet(v) {
    try { window.localStorage.setItem(KEY, v); return true; } catch (e) { return false; }
  }

  function storageDel() {
    try { window.localStorage.removeItem(KEY); return true; } catch (e) { return false; }
  }

  function readRaw() {
    // Prefer localStorage, fallback to cookie
    var v = storageGet();
    if (v) return v;
    return cookieGet(KEY);
  }

  function writeRaw(v) {
    // Try localStorage first, then cookie; keep cookie mirrored either way
    var ok = storageSet(v);
    cookieSet(KEY, v, COOKIE_DAYS);
    return ok;
  }

  function normalizeCut(cut) {
    var s = String(cut || '').trim();
    if (!s) return '';
    // allow "Slim Cut" / "Classic Cut" inputs
    s = s.replace(/\s+cut$/i, '').trim();
    return s;
  }
  // Only established cuts belong in the sitewide quick-add preference.
  // New/experimental cuts keep working on their PDPs without replacing it.
  var STANDARD_CUTS = {
    'classic': 'Classic',
    'slim': 'Slim',
    'tower': 'Tower',
    'tear drop': 'Tear Drop',
    'teardrop': 'Tear Drop'
  };

  function standardCut(cut) {
    var key = normalizeCut(cut).replace(/\s+/g, ' ').toLowerCase();
    return STANDARD_CUTS[key] || '';
  }

  function cutFromPatch(patch, currentCut) {
    var hasPouchStyle = patch.pouchStyle != null;
    var hasCut = patch.cut != null;
    if (!hasPouchStyle && !hasCut) return currentCut || '';

    var requested = normalizeCut(hasPouchStyle ? patch.pouchStyle : patch.cut);
    if (!requested) return '';

    return standardCut(requested) || standardCut(currentCut) || 'Classic';
  }

  function normalizeSize(size) {
    var s = String(size || '').trim();
    if (!s) return '';
    // allow "XL 40-42" / "6XL 60–62" etc; keep first token as canonical
    s = s.split(/\s+/)[0].trim();
    return s;
  }

  function normalizeStrapStyle(value) {
    return String(value || '').trim();
  }

  function normalizeAdjustment(value) {
    var s = String(value || '').trim();
    return /^[-+]?[12]$/.test(s) ? (s.charAt(0) === '-' ? s : '+' + s.replace('+', '')) : '';
  }

  function canon(obj) {
    obj = obj && typeof obj === 'object' ? obj : {};
    var cut = normalizeCut(obj.cut);
    var size = normalizeSize(obj.size);
    var strapStyle = normalizeStrapStyle(obj.thongStrapStyle || obj.strapStyle);
    var pouchAdjustment = normalizeAdjustment(obj.pouchAdjustment || (obj.adjustments && obj.adjustments.pouch));
    var backStrapAdjustment = normalizeAdjustment(obj.backStrapAdjustment || (obj.adjustments && obj.adjustments.backStrap));
    if (!cut && !size && !strapStyle && !pouchAdjustment && !backStrapAdjustment) return null;
    return {
      cut: cut,
      size: size,
      pouchStyle: cut,
      waistSize: size,
      thongStrapStyle: strapStyle,
      pouchAdjustment: pouchAdjustment,
      backStrapAdjustment: backStrapAdjustment,
      adjustments: {
        pouch: pouchAdjustment,
        backStrap: backStrapAdjustment
      },
      options: {
        'Pouch Style': cut,
        'Waist Size': size,
        'Thong Strap Style': strapStyle
      }
    };
  }

  function same(a, b) {
    a = a || { cut: '', size: '' };
    b = b || { cut: '', size: '' };
    return String(a.cut || '') === String(b.cut || '') &&
      String(a.size || '') === String(b.size || '') &&
      String(a.thongStrapStyle || '') === String(b.thongStrapStyle || '') &&
      String(a.pouchAdjustment || '') === String(b.pouchAdjustment || '') &&
      String(a.backStrapAdjustment || '') === String(b.backStrapAdjustment || '');
  }

  function get() {
    var raw = readRaw();
    if (!raw) return null;

    var obj = safeJsonParse(raw);
    var c = canon(obj);
    if (!c) return null;

    // Heal a preference saved before unsupported cuts were filtered.
    if (c.cut) {
      var safeCut = standardCut(c.cut) || 'Classic';
      if (safeCut !== c.cut) {
        c.cut = safeCut;
        c.pouchStyle = safeCut;
        c.options['Pouch Style'] = safeCut;
        writeRaw(JSON.stringify(c));
      }
    }

    return c;
  }

  function _commit(next, emit) {
    var cur = get();
    if (next == null) {
      // Clearing state
      // Only clear if something exists
      if (cur == null) return null;

      storageDel();
      cookieDel(KEY);

      if (emit) {
        try { window.dispatchEvent(new CustomEvent(EVT, { detail: { cut: '', size: '' } })); } catch (e) {}
      }
      return null;
    }

    // Only write + emit on actual change
    if (same(cur, next)) return cur;

    writeRaw(JSON.stringify(next));

    if (emit) {
      try { window.dispatchEvent(new CustomEvent(EVT, { detail: next })); } catch (e) {}
    }
    return next;
  }

  function set(patch) {
    // Patch can be partial: {cut} or {size} or both
    var cur = get() || { cut: '', size: '' };
    patch = patch && typeof patch === 'object' ? patch : {};

    var next = canon({
      cut: cutFromPatch(patch, cur.cut),
      size: (patch.waistSize != null ? patch.waistSize : (patch.size != null ? patch.size : cur.size)),
      thongStrapStyle: (patch.thongStrapStyle != null ? patch.thongStrapStyle : cur.thongStrapStyle),
      pouchAdjustment: (patch.pouchAdjustment != null ? patch.pouchAdjustment : cur.pouchAdjustment),
      backStrapAdjustment: (patch.backStrapAdjustment != null ? patch.backStrapAdjustment : cur.backStrapAdjustment)
    });

    // If both fields become empty, treat as clear
    return _commit(next, true);
  }

  function setSilent(patch) {
    var cur = get() || { cut: '', size: '' };
    patch = patch && typeof patch === 'object' ? patch : {};

    var next = canon({
      cut: cutFromPatch(patch, cur.cut),
      size: (patch.waistSize != null ? patch.waistSize : (patch.size != null ? patch.size : cur.size)),
      thongStrapStyle: (patch.thongStrapStyle != null ? patch.thongStrapStyle : cur.thongStrapStyle),
      pouchAdjustment: (patch.pouchAdjustment != null ? patch.pouchAdjustment : cur.pouchAdjustment),
      backStrapAdjustment: (patch.backStrapAdjustment != null ? patch.backStrapAdjustment : cur.backStrapAdjustment)
    });

    return _commit(next, false);
  }

  function clear() {
    return _commit(null, true);
  }

  function raw() {
    return readRaw();
  }

  window.JMT_PREF = {
    KEY: KEY,
    EVT: EVT,
    get: get,
    set: set,
    setSilent: setSilent,
    clear: clear,
    raw: raw
  };
})();
